<script setup>
import { ArrowLongDownIcon, ArrowLongUpIcon } from "@heroicons/vue/24/solid";
import { FlexRender } from "@tanstack/vue-table";

const props = defineProps({
  table: {
    required: true,
    type: Object,
  },
  withFooter: {
    required: false,
    type: Boolean,
    default: false,
  },
  withRowNumber: {
    required: false,
    type: Boolean,
    default: false,
  },
  forceScroll: {
    required: false,
    type: Boolean,
    default: false,
  },
  renderRowSubComponent: {
    required: false,
    type: Function,
    default: null,
  },
  wrapHeaders: {
    required: false,
    type: Boolean,
    default: false,
  },
});
</script>

<template>
  <div
    class="relative max-3xl:h-full max-3xl:max-h-[600px] max-3xl:overflow-x-auto"
    :class="{ '!max-h-[600px] !overflow-x-auto': props.forceScroll }"
  >
    <table class="relative w-full table-auto border-collapse text-left">
      <thead
        class="sticky top-16 z-10 bg-white text-xs uppercase max-3xl:top-0"
        :class="{ '!top-0': props.forceScroll }"
      >
        <tr
          class="h-12"
          v-for="headerGroup in props.table.getHeaderGroups()"
          :key="headerGroup.id"
        >
          <th v-if="props.withRowNumber" class="h-[inherit] p-0">
            <div class="h-12 border-b border-t border-gray-200 px-4 py-3">
              &nbsp;
            </div>
          </th>

          <th
            scope="col"
            class="h-[inherit] p-0"
            :class="{
              'whitespace-nowrap': !props.wrapHeaders,
              'cursor-pointer': header.column.getCanSort(),
              'sticky right-0 bg-white before:pointer-events-none before:absolute before:-left-[5px] before:top-0 before:block before:h-full before:w-[5px] before:bg-gradient-to-l before:from-gray-200 before:to-gray-50 before:opacity-100 before:content-[\'\']':
                header.column.getIsPinned(),
            }"
            :style="{ width: `${header.column.columnDef.width}px` }"
            v-for="header in headerGroup.headers"
            :key="header.id"
            @click="(event) => header.column.getToggleSortingHandler()?.(event)"
          >
            <div
              class="border-b border-t border-gray-200 px-4 py-3"
              :class="{
                'h-12': !props.wrapHeaders,
                'h-full': props.wrapHeaders,
                flex: props.wrapHeaders,
                'items-center': props.wrapHeaders,
              }"
            >
              <div
                v-if="
                  typeof header.column.columnDef.header === 'object' &&
                  header.column.columnDef.header.__v_isVNode
                "
                :class="{
                  'h-6': !props.wrapHeaders,
                  'h-auto': props.wrapHeaders,
                }"
              >
                <FlexRender
                  v-if="!header.isPlaceholder"
                  :render="header.column.columnDef.header"
                  :props="header.getContext()"
                />
              </div>

              <div
                v-else
                :class="[
                  'inline-flex items-center gap-1',
                  header.column.columnDef.headerClasses,
                  {
                    'h-6': !props.wrapHeaders,
                  },
                ]"
              >
                <FlexRender
                  v-if="!header.isPlaceholder"
                  :render="header.column.columnDef.header"
                  :props="header.getContext()"
                />

                <div v-if="header.column.getCanSort()" class="inline-flex">
                  <ArrowLongUpIcon
                    class="h-6 w-3"
                    :class="{
                      'opacity-100': header.column.getIsSorted() === 'asc',
                      'opacity-40': header.column.getIsSorted() !== 'asc',
                    }"
                  />

                  <ArrowLongDownIcon
                    class="h-6 w-3"
                    :class="{
                      'opacity-100': header.column.getIsSorted() === 'desc',
                      'opacity-40': header.column.getIsSorted() !== 'desc',
                    }"
                  />
                </div>
              </div>
            </div>
          </th>
        </tr>
      </thead>

      <tbody>
        <template
          :key="row.id"
          v-for="(row, index) in props.table.getRowModel().rows"
        >
          <tr
            class="text-sm text-gray-500 duration-150"
            :class="{
              'bg-white': index % 2,
              'bg-gray-50': !(index % 2),
              '!bg-gray-400 !text-gray-800': row.getIsExpanded(),
              '!bg-gray-300 !text-gray-600': row.depth > 0,
            }"
          >
            <td v-if="props.withRowNumber">
              <div
                class="flex h-full items-center justify-center whitespace-nowrap p-4 text-center"
              >
                <div class="text-lg font-bold">
                  {{
                    table.getState().pagination.pageIndex *
                      table.getState().pagination.pageSize +
                    index +
                    1
                  }}
                </div>
              </div>
            </td>

            <td
              v-for="cell in row.getVisibleCells()"
              :key="cell.id"
              class="min-h-[60px] p-0"
              :class="{
                'bg-white': cell.column.getIsPinned() && index % 2,
                'bg-gray-50': cell.column.getIsPinned() && !(index % 2),
                'sticky right-0 before:pointer-events-none before:absolute before:-left-[5px] before:top-0 before:block before:h-full before:w-[5px] before:bg-gradient-to-l before:from-gray-200 before:to-gray-50 before:opacity-100 before:content-[\'\']':
                  cell.column.getIsPinned(),
              }"
            >
              <FlexRender
                :render="cell.column.columnDef.cell"
                :props="cell.getContext()"
              />
            </td>
          </tr>
          <tr v-if="props.renderRowSubComponent && row.getIsExpanded()">
            <td :colspan="row.getVisibleCells().length">
              <component :is="renderRowSubComponent(row)" />
            </td>
          </tr>
        </template>
      </tbody>

      <tfoot v-if="props.withFooter" class="mt-12">
        <tr
          v-for="footerGroup in props.table.getFooterGroups()"
          :key="footerGroup.id"
          class="text-sm text-gray-500"
        >
          <th v-if="props.withRowNumber" class="px-4 py-3">&nbsp;</th>

          <td
            v-for="header in footerGroup.headers"
            :key="header.id"
            class="min-h-[60px] p-0"
          >
            <FlexRender
              :render="header.column.columnDef.footer"
              :props="header.getContext()"
            />
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>
